import { Preferences } from '@capacitor/preferences';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';
import type { Session, User } from '@supabase/supabase-js';

/**
 * Service for managing auth tokens with secure storage
 * Handles encryption, persistence, and lifecycle management of auth tokens
 */
export class SecureAuthTokenService {
  private static instance: SecureAuthTokenService;
  private isNative = Capacitor.isNativePlatform();
  
  // Storage keys
  private static readonly KEYS = {
    ACCESS_TOKEN: 'matmonster-access-token',
    REFRESH_TOKEN: 'matmonster-refresh-token', 
    SESSION_DATA: 'matmonster-session-data',
    USER_DATA: 'matmonster-user-data',
    TOKEN_EXPIRES_AT: 'matmonster-token-expires-at',
    LAST_REFRESH: 'matmonster-last-refresh',
  } as const;

  private constructor() {}

  static getInstance(): SecureAuthTokenService {
    if (!SecureAuthTokenService.instance) {
      SecureAuthTokenService.instance = new SecureAuthTokenService();
    }
    return SecureAuthTokenService.instance;
  }

  /**
   * Store auth session securely
   */
  async storeSession(session: Session): Promise<void> {
    try {

      
      const promises: Promise<void>[] = [];
      
      // Store access token (most sensitive)
      if (session.access_token) {
        promises.push(this.setSecureItem(SecureAuthTokenService.KEYS.ACCESS_TOKEN, session.access_token));
      }
      
      // Store refresh token (very sensitive)
      if (session.refresh_token) {
        promises.push(this.setSecureItem(SecureAuthTokenService.KEYS.REFRESH_TOKEN, session.refresh_token));
      }
      
      // Store session metadata (less sensitive)
      const sessionMetadata = {
        token_type: session.token_type,
        expires_in: session.expires_in,
        expires_at: session.expires_at,
        provider_token: session.provider_token,
        provider_refresh_token: session.provider_refresh_token,
      };
      promises.push(this.setRegularItem(SecureAuthTokenService.KEYS.SESSION_DATA, JSON.stringify(sessionMetadata)));
      
      // Store user data (less sensitive)
      if (session.user) {
        promises.push(this.setRegularItem(SecureAuthTokenService.KEYS.USER_DATA, JSON.stringify(session.user)));
      }
      
      // Store expiration timestamp for quick checks
      if (session.expires_at) {
        promises.push(this.setRegularItem(SecureAuthTokenService.KEYS.TOKEN_EXPIRES_AT, session.expires_at.toString()));
      }
      
      // Store last refresh timestamp
      promises.push(this.setRegularItem(SecureAuthTokenService.KEYS.LAST_REFRESH, Date.now().toString()));
      
      await Promise.all(promises);

    } catch (error) {
      console.error('🔐 Error storing session:', error);
      throw error;
    }
  }

  /**
   * Retrieve stored session
   */
  async getStoredSession(): Promise<Session | null> {
    try {

      
      const [accessToken, refreshToken, sessionDataStr, userDataStr, expiresAtStr] = await Promise.all([
        this.getSecureItem(SecureAuthTokenService.KEYS.ACCESS_TOKEN),
        this.getSecureItem(SecureAuthTokenService.KEYS.REFRESH_TOKEN),
        this.getRegularItem(SecureAuthTokenService.KEYS.SESSION_DATA),
        this.getRegularItem(SecureAuthTokenService.KEYS.USER_DATA),
        this.getRegularItem(SecureAuthTokenService.KEYS.TOKEN_EXPIRES_AT),
      ]);

      if (!accessToken || !refreshToken) {

        return null;
      }

      let sessionMetadata: Record<string, unknown> = {};
      let user: User | null = null;

      try {
        if (sessionDataStr) {
          sessionMetadata = JSON.parse(sessionDataStr);
        }
        if (userDataStr) {
          user = JSON.parse(userDataStr);
        }
      } catch (parseError) {
        console.warn('🔐 Error parsing stored session data:', parseError);
      }

      const session: Session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: sessionMetadata.token_type || 'bearer',
        expires_in: sessionMetadata.expires_in || 3600,
        expires_at: expiresAtStr ? parseInt(expiresAtStr) : undefined,
        provider_token: sessionMetadata.provider_token,
        provider_refresh_token: sessionMetadata.provider_refresh_token,
        user: user!,
      };


      return session;
    } catch (error) {
      console.error('🔐 Error retrieving stored session:', error);
      return null;
    }
  }

  /**
   * Check if stored tokens are expired
   */
  async isTokenExpired(): Promise<boolean> {
    try {
      const expiresAtStr = await this.getRegularItem(SecureAuthTokenService.KEYS.TOKEN_EXPIRES_AT);
      if (!expiresAtStr) return true;

      const expiresAt = parseInt(expiresAtStr) * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minute buffer

      return now >= (expiresAt - bufferTime);
    } catch (error) {
      console.error('🔐 Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Clear all stored auth data
   */
  async clearStoredAuth(): Promise<void> {
    try {

      
      const clearPromises = Object.values(SecureAuthTokenService.KEYS).map(async (key) => {
        try {
          await this.removeSecureItem(key);
          await this.removeRegularItem(key);
        } catch (error) {
          console.warn(`🔐 Error clearing ${key}:`, error);
        }
      });

      await Promise.all(clearPromises);

    } catch (error) {
      console.error('🔐 Error clearing auth data:', error);
      throw error;
    }
  }

  /**
   * Get storage statistics for debugging
   */
  async getStorageStats(): Promise<{
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasSessionData: boolean;
    hasUserData: boolean;
    isExpired: boolean;
    lastRefresh: string | null;
    platform: string;
    secureStorageAvailable: boolean;
  }> {
    const [accessToken, refreshToken, sessionData, userData, lastRefresh] = await Promise.all([
      this.getSecureItem(SecureAuthTokenService.KEYS.ACCESS_TOKEN),
      this.getSecureItem(SecureAuthTokenService.KEYS.REFRESH_TOKEN),
      this.getRegularItem(SecureAuthTokenService.KEYS.SESSION_DATA),
      this.getRegularItem(SecureAuthTokenService.KEYS.USER_DATA),
      this.getRegularItem(SecureAuthTokenService.KEYS.LAST_REFRESH),
    ]);

    const isExpired = await this.isTokenExpired();
    
    let secureStorageAvailable = false;
    if (this.isNative) {
      try {
        await SecureStorage.set('test-key', 'test-value');
        await SecureStorage.remove('test-key');
        secureStorageAvailable = true;
      } catch {
        secureStorageAvailable = false;
      }
    }

    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasSessionData: !!sessionData,
      hasUserData: !!userData,
      isExpired,
      lastRefresh: lastRefresh ? new Date(parseInt(lastRefresh)).toISOString() : null,
      platform: Capacitor.getPlatform(),
      secureStorageAvailable,
    };
  }

  /**
   * Store item in secure storage (for sensitive data)
   */
  private async setSecureItem(key: string, value: string): Promise<void> {
    if (this.isNative) {
      try {
        await SecureStorage.set(key, value);
        return;
      } catch (error) {
        console.warn(`🔐 Secure storage failed for ${key}, falling back to preferences:`, error);
      }
    }
    
    // Fallback to regular preferences
    await Preferences.set({ key, value });
  }

  /**
   * Get item from secure storage (for sensitive data)
   */
  private async getSecureItem(key: string): Promise<string | null> {
    if (this.isNative) {
      try {
        const result = await SecureStorage.get(key);
        return typeof result === 'string' ? result : null;
      } catch (error) {
        console.warn(`🔐 Secure storage failed for ${key}, trying preferences:`, error);
      }
    }
    
    // Fallback to regular preferences
    const { value } = await Preferences.get({ key });
    return value;
  }

  /**
   * Remove item from secure storage
   */
  private async removeSecureItem(key: string): Promise<void> {
    if (this.isNative) {
      try {
        await SecureStorage.remove(key);
      } catch (error) {
        console.warn(`🔐 Error removing from secure storage ${key}:`, error);
      }
    }
  }

  /**
   * Store item in regular preferences (for non-sensitive data)
   */
  private async setRegularItem(key: string, value: string): Promise<void> {
    await Preferences.set({ key, value });
  }

  /**
   * Get item from regular preferences
   */
  private async getRegularItem(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key });
    return value;
  }

  /**
   * Remove item from regular preferences
   */
  private async removeRegularItem(key: string): Promise<void> {
    await Preferences.remove({ key });
  }
}
